/* ═══════════════════════════════════════════════════════════════
   ADAPTADOR LEGACY — adminStaff
   Lee del motor canónico persons.ts y exporta en el formato
   antiguo esperado por la página /admin/staff.
   ═══════════════════════════════════════════════════════════════ */

import { canonicalPersons } from './persons';
import type { CanonicalPerson, StaffRole as CanonicalStaffRole, StaffPermission as CanonicalStaffPermission } from './persons';

/* ─── Tipos legacy (sin cambios) ─── */
export type StaffRole = 'administrador' | 'locutor' | 'moderador' | 'marketing' | 'ventas' | 'invitado';

export type StaffPermission = 'cabina' | 'chat' | 'contenido' | 'monetizacion' | 'analytics' | 'configuracion';

export type StaffStatus = 'active' | 'inactive' | 'invited' | 'suspended';

export interface StaffMember {
  id: number;
  name: string;
  role: StaffRole;
  photoUrl: string;
  bio: string;
  email: string;
  socialLinks: {
    instagram?: string;
    twitter?: string;
    youtube?: string;
    tiktok?: string;
    website?: string;
  };
  assignedPrograms: string[];
  permissions: StaffPermission[];
  joinedAt: string;
  status: StaffStatus;
  invitedAt?: string;
  suspendedAt?: string;
  suspensionReason?: string;
}

export const roleLabels: Record<StaffRole, string> = {
  administrador: 'Administrador',
  locutor: 'Locutor',
  moderador: 'Moderador',
  marketing: 'Marketing',
  ventas: 'Ventas',
  invitado: 'Invitado',
};

export const roleColors: Record<StaffRole, string> = {
  administrador: 'bg-amber-100 text-amber-800',
  locutor: 'bg-accent-100 text-accent-700',
  moderador: 'bg-sky-100 text-sky-700',
  marketing: 'bg-rose-100 text-rose-700',
  ventas: 'bg-emerald-100 text-emerald-700',
  invitado: 'bg-violet-100 text-violet-700',
};

export const roleDescriptions: Record<StaffRole, string> = {
  administrador: 'Control total del sistema. Gestiona staff, programas, monetización y configuración.',
  locutor: 'Accede a cabina, lives, eventos. Puede crear y gestionar su contenido.',
  moderador: 'Modera el chat y la comunidad. Aprueba mensajes y gestiona la cabina.',
  marketing: 'Gestiona campañas, redes sociales, publicaciones y eventos promocionales.',
  ventas: 'Accede a marketplace, patrocinadores y métricas de revenue.',
  invitado: 'Acceso temporal limitado. Solo lectura en áreas asignadas.',
};

export const permissionLabels: Record<StaffPermission, string> = {
  cabina: 'Cabina',
  chat: 'Chat',
  contenido: 'Contenido',
  monetizacion: 'Monetización',
  analytics: 'Analytics',
  configuracion: 'Configuración',
};

export const permissionColors: Record<StaffPermission, string> = {
  cabina: 'bg-sky-100 text-sky-700',
  chat: 'bg-fuchsia-100 text-fuchsia-700',
  contenido: 'bg-accent-100 text-accent-700',
  monetizacion: 'bg-amber-100 text-amber-700',
  analytics: 'bg-emerald-100 text-emerald-700',
  configuracion: 'bg-secondary-100 text-secondary-700',
};

export const defaultRolePermissions: Record<StaffRole, StaffPermission[]> = {
  administrador: ['cabina', 'chat', 'contenido', 'monetizacion', 'analytics', 'configuracion'],
  locutor: ['cabina', 'chat', 'contenido', 'analytics'],
  moderador: ['cabina', 'chat'],
  marketing: ['contenido', 'analytics'],
  ventas: ['monetizacion', 'analytics'],
  invitado: [],
};

export const statusLabels: Record<StaffStatus, string> = {
  active: 'Activo',
  inactive: 'Inactivo',
  invited: 'Invitado',
  suspended: 'Suspendido',
};

export const statusColors: Record<StaffStatus, string> = {
  active: 'bg-emerald-100 text-emerald-700',
  inactive: 'bg-foreground-200 text-foreground-600',
  invited: 'bg-amber-100 text-amber-700',
  suspended: 'bg-red-100 text-red-700',
};

/* ─── Conversión ─── */
function mapStaffRole(cr: CanonicalStaffRole): StaffRole {
  const map: Record<string, StaffRole> = {
    administrador: 'administrador',
    locutor: 'locutor',
    moderador: 'moderador',
    marketing: 'marketing',
    ventas: 'ventas',
    invitado: 'invitado',
  };
  return map[cr] || 'invitado';
}

function personToStaff(person: CanonicalPerson): StaffMember {
  const sp = person.staffProfile!;
  return {
    id: parseInt(person.id.replace('p-', ''), 10),
    name: person.displayName,
    role: mapStaffRole(sp.role),
    photoUrl: person.avatar,
    bio: person.bio,
    email: person.email,
    socialLinks: sp.socialLinks,
    assignedPrograms: sp.assignedPrograms,
    permissions: [...sp.permissions] as StaffPermission[],
    joinedAt: sp.joinedAt,
    status: sp.staffStatus,
    invitedAt: sp.invitedAt,
    suspendedAt: sp.suspendedAt,
    suspensionReason: sp.suspensionReason,
  };
}

/* ─── Los 11 miembros de staff, desde el motor canónico ─── */
const legacyStaffPersonIds = [
  'p-001',  // Carlos Mendoza - admin
  'p-002',  // Valentina Ríos - locutor
  'p-004',  // Andrés Castillo - locutor
  'p-010',  // María José Herrera - marketing
  'p-011',  // Diego Fuentes - moderador (suspended)
  'p-012',  // Lucía Montenegro - ventas
  'p-003',  // Roberto Aguilar - locutor
  'p-006',  // Sofía Linares - marketing
  'p-013',  // Pedro Navarro - invitado
  'p-005',  // Camila Ortega - locutor
  'p-007',  // Javier Solís - moderador
];

function buildAdminStaff(): StaffMember[] {
  return legacyStaffPersonIds
    .map((id) => {
      const person = canonicalPersons.find((p) => p.id === id);
      if (!person || !person.staffProfile) return null;
      return personToStaff(person);
    })
    .filter(Boolean) as StaffMember[];
}

export const adminStaff: StaffMember[] = buildAdminStaff();